import { useEffect, useRef, useState } from 'react';
import { Play, Pause, ChevronsLeft, ChevronsRight, RotateCcw, Minus, Plus } from 'lucide-react';

/**
 * Clip trimmer for video review.
 *
 * The in/out points chosen here are what the assembly stage cuts to. In v1 they
 * were approximate: the trims were handed to ffmpeg's concat demuxer as
 * `inpoint`/`outpoint` on un-normalised H.264, which seeks at packet level, so
 * the cut landed on the nearest keyframe rather than the chosen moment. The
 * worker now re-encodes each segment, making these frame-accurate.
 */

const STEP = 0.05;
const MIN_GAP = 0.2;

const snap = (v: number) => Number((Math.round(v / STEP) * STEP).toFixed(2));

export default function TrimPlayer({
  src,
  inPoint,
  outPoint,
  onChange,
  onError,
}: {
  src: string;
  inPoint: number;
  outPoint: number;
  onChange: (points: { inPoint: number; outPoint: number }) => void;
  /** Fired when the source won't load, so the caller can try its fallback. */
  onError?: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(8);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);

  // While a handle is being dragged the values live here, and onChange fires
  // once on release — the parent persists every change to the server, and a
  // write per pointermove would be write-spam.
  const [dragging, setDragging] = useState<'in' | 'out' | null>(null);
  const [dragIn, setDragIn] = useState(inPoint);
  const [dragOut, setDragOut] = useState(outPoint);

  const shownIn = dragging ? dragIn : inPoint;
  const shownOut = dragging ? dragOut : outPoint;

  // Loop playback within the trim so the reviewer hears exactly what ships.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onTime = () => {
      setCurrent(video.currentTime);
      // Loop only during playback. Snapping while paused made it impossible to
      // scrub past the out-point, so "Set end" could only ever shrink the trim.
      if (!video.paused && video.currentTime >= outPoint) {
        video.currentTime = inPoint;
        void video.play();
      }
    };
    const onMeta = () => setDuration(video.duration || 8);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);

    video.addEventListener('timeupdate', onTime);
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);

    return () => {
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, [inPoint, outPoint]);

  const timeAtPointer = (clientX: number): number => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return snap(ratio * (duration || 8));
  };

  const startDrag = (which: 'in' | 'out') => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(which);
    setDragIn(inPoint);
    setDragOut(outPoint);

    const move = (ev: PointerEvent) => {
      const t = timeAtPointer(ev.clientX);
      if (which === 'in') {
        setDragIn(Math.max(0, Math.min(t, snap(outPoint - MIN_GAP))));
      } else {
        setDragOut(Math.min(duration, Math.max(t, snap(inPoint + MIN_GAP))));
      }
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      const t = timeAtPointer(ev.clientX);
      setDragging(null);
      if (which === 'in') {
        onChange({ inPoint: Math.max(0, Math.min(t, snap(outPoint - MIN_GAP))), outPoint });
      } else {
        onChange({ inPoint, outPoint: Math.min(duration, Math.max(t, snap(inPoint + MIN_GAP))) });
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const nudge = (which: 'in' | 'out', delta: number) => {
    if (which === 'in') {
      const next = snap(Math.max(0, Math.min(inPoint + delta, outPoint - MIN_GAP)));
      onChange({ inPoint: next, outPoint });
    } else {
      const next = snap(Math.min(duration, Math.max(outPoint + delta, inPoint + MIN_GAP)));
      onChange({ inPoint, outPoint: next });
    }
  };

  const setIn = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = snap(Math.min(video.currentTime, outPoint - MIN_GAP));
    onChange({ inPoint: Math.max(0, next), outPoint });
  };

  const setOut = () => {
    const video = videoRef.current;
    if (!video) return;
    const next = snap(Math.max(video.currentTime, inPoint + MIN_GAP));
    onChange({ inPoint, outPoint: Math.min(duration, next) });
  };

  const reset = () => onChange({ inPoint: 0.01, outPoint: snap(duration - 0.15) });

  const toggle = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      if (video.currentTime < inPoint || video.currentTime >= outPoint) video.currentTime = inPoint;
      void video.play();
    } else {
      video.pause();
    }
  };

  const pct = (t: number) => `${Math.min(100, Math.max(0, (t / (duration || 8)) * 100))}%`;

  return (
    <div className="space-y-3">
      <div className="flex justify-center bg-stone-900 rounded-2xl overflow-hidden">
        <video
          ref={videoRef}
          src={src}
          playsInline
          preload="metadata"
          className="max-h-[45vh] w-auto"
          onClick={toggle}
          onError={onError}
        />
      </div>

      <div
        ref={trackRef}
        className="relative h-9 bg-stone-100 rounded-lg select-none"
      >
        {/* Selected range */}
        <div
          className="absolute inset-y-0 bg-natural-sage/20 rounded-sm"
          style={{ left: pct(shownIn), width: `calc(${pct(shownOut)} - ${pct(shownIn)})` }}
        />
        {/* Playhead */}
        <div
          className="absolute inset-y-0 w-0.5 bg-natural-rosetext"
          style={{ left: pct(current) }}
        />
        {/* Scrub (whole track, under the handles) */}
        <input
          type="range"
          min={0}
          max={duration || 8}
          step={STEP}
          value={current}
          onChange={(e) => {
            const video = videoRef.current;
            if (video) video.currentTime = Number(e.target.value);
          }}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
          aria-label="Scrub"
        />
        {/* Draggable in/out handles, above the scrub input */}
        <div
          onPointerDown={startDrag('in')}
          title="Drag to adjust the start point (0.05s steps)"
          className={`absolute inset-y-0 w-3 -ml-1.5 flex items-center justify-center cursor-ew-resize touch-none z-10 ${
            dragging === 'in' ? 'scale-110' : ''
          }`}
          style={{ left: pct(shownIn) }}
        >
          <div className="w-1.5 h-full rounded-full bg-natural-sage shadow-sm" />
        </div>
        <div
          onPointerDown={startDrag('out')}
          title="Drag to adjust the end point (0.05s steps)"
          className={`absolute inset-y-0 w-3 -ml-1.5 flex items-center justify-center cursor-ew-resize touch-none z-10 ${
            dragging === 'out' ? 'scale-110' : ''
          }`}
          style={{ left: pct(shownOut) }}
        >
          <div className="w-1.5 h-full rounded-full bg-natural-sage shadow-sm" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={toggle} className="zen-btn-ghost py-1.5 px-3 text-xs">
          {playing ? <Pause size={13} /> : <Play size={13} />}
          {playing ? 'Pause' : 'Play trim'}
        </button>

        <button onClick={setIn} className="zen-btn-ghost py-1.5 px-3 text-xs" title="Set the start point to the current playhead">
          <ChevronsLeft size={13} />
          Set start
        </button>

        <button onClick={setOut} className="zen-btn-ghost py-1.5 px-3 text-xs" title="Set the end point to the current playhead">
          <ChevronsRight size={13} />
          Set end
        </button>

        <button onClick={reset} className="zen-btn-ghost py-1.5 px-3 text-xs" title="Reset to the full clip">
          <RotateCcw size={13} />
        </button>

        {/* Fine-grained nudges, 0.05s per press */}
        <span className="ml-auto flex items-center gap-1 font-mono text-[11px] text-natural-text/55 tabular-nums">
          <span className="text-natural-text/40">start</span>
          <button onClick={() => nudge('in', -STEP)} className="p-1 rounded hover:bg-stone-100 cursor-pointer" title="Start −0.05s">
            <Minus size={11} />
          </button>
          {shownIn.toFixed(2)}
          <button onClick={() => nudge('in', STEP)} className="p-1 rounded hover:bg-stone-100 cursor-pointer" title="Start +0.05s">
            <Plus size={11} />
          </button>
          <span className="text-natural-text/40 ml-2">end</span>
          <button onClick={() => nudge('out', -STEP)} className="p-1 rounded hover:bg-stone-100 cursor-pointer" title="End −0.05s">
            <Minus size={11} />
          </button>
          {shownOut.toFixed(2)}
          <button onClick={() => nudge('out', STEP)} className="p-1 rounded hover:bg-stone-100 cursor-pointer" title="End +0.05s">
            <Plus size={11} />
          </button>
          <span className="text-natural-text/35 ml-1">({(shownOut - shownIn).toFixed(2)}s)</span>
        </span>
      </div>
    </div>
  );
}
